<template>
    <div
        v-click-away="handleCloseMenu"
        tabindex="0"
        class="z-20 focus:outline-hidden"
        :class="{
            'ml-4 md:ml-4': !sidebarOpened,
            'ml-4 md:ml-5': sidebarOpened,
        }"
        @keydown.esc="isOpen = false"
    >
        <div
            class="flex max-w-[180px] cursor-pointer items-center sm:w-[205px] sm:max-w-[205px] sm:justify-between xl:w-[170px]"
            @click="handleProfileMenu"
        >
            <div class="hidden items-center sm:flex">
                <div
                    class="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                >
                    <img
                        :src="current.profile_photo_url"
                        class="h-[30px] w-[30px] rounded-full bg-white object-cover"
                        alt="Team menu profile photo url"
                    />
                </div>

                <div
                    v-if="sidebarOpened"
                    class="ml-2 mr-[10px] select-none truncate text-sm font-medium text-gray-700 md:ml-4 md:mr-[38px] xl:hidden"
                >
                    {{ current.name }}
                </div>

                <div
                    class="ml-2 mr-[10px] hidden select-none truncate text-sm font-medium text-gray-700 md:ml-4 md:mr-[38px] xl:block"
                >
                    {{ current.name }}
                </div>
            </div>

            <div
                class="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300 sm:hidden"
            >
                <img
                    :src="current.profile_photo_url"
                    class="h-[30px] w-[30px] rounded-full bg-white object-cover"
                    alt="Team menu profile photo url 2"
                />
            </div>

            <div
                class="ml-2 mr-[10px] select-none truncate text-sm font-medium text-white sm:hidden md:ml-4 md:mr-[38px]"
            >
                {{ current.name }}
            </div>
        </div>

        <transition
            enter-active-class="transition duration-1000 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isOpen"
                class="fixed left-0 top-[56px] z-498 h-full w-[calc(100vw)] overflow-y-scroll bg-gray-800 bg-opacity-30 backdrop-blur-[2px] backdrop-filter"
                @click.self="handleCloseMenu"
            ></div>
        </transition>

        <transition
            enter-active-class="transition duration-500 ease-out delay-100 md:duration-100"
            enter-from-class="transform translate-y-40 opacity-0 md:scale-95 md:translate-y-0"
            enter-to-class="transform translate-y-0 opacity-100 md:scale-100"
            leave-active-class="transition duration-200 ease-in md:duration-75"
            leave-from-class="transform opacity-100 md:scale-100"
            leave-to-class="transform translate-y-40 opacity-0 md:scale-95 md:translate-y-0"
        >
            <div
                v-if="isOpen"
                v-touch:swipe.bottom="handleCloseMenu"
                class="fixed inset-x-0 bottom-0 z-499 mx-auto w-screen max-w-[640px] rounded-t-xl bg-white shadow-lg focus:outline-hidden md:absolute md:bottom-0 md:ml-0 md:mt-2 md:w-[336px] md:max-w-none md:origin-top-right md:rounded-md md:ring-1 md:ring-black md:ring-opacity-5"
                :class="{
                    'md:left-[40px] xl:left-[170px]': !sidebarOpened,
                    'md:left-[205px]': sidebarOpened,
                }"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="options-menu"
            >
                <div class="relative mx-auto divide-y divide-gray-200">
                    <div
                        v-if="allTeams.length"
                        class="custom-scroll mb-[7px] max-h-[140px] overflow-y-scroll pt-2.5 md:max-h-full md:overflow-hidden"
                        role="none"
                    >
                        <div class="px-6 text-xs uppercase text-gray-400">
                            Accounts
                        </div>

                        <div
                            v-for="(team, i) in allTeams"
                            :key="i"
                            class="group flex cursor-pointer items-center justify-between py-3 pl-6 pr-6 hover:bg-gray-50 hover:text-gray-900 md:pr-4"
                            @click="switchTeam(team.id)"
                        >
                            <div class="flex items-center">
                                <div
                                    class="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                                >
                                    <img
                                        :src="team.profile_photo_url"
                                        class="h-[30px] w-[30px] rounded-full object-cover"
                                        alt="Team menu profile photo url 3"
                                    />
                                </div>

                                <p
                                    class="ml-2 text-sm font-medium text-gray-500 md:ml-4"
                                >
                                    {{ team.name }}
                                </p>
                                <div
                                    v-if="team.user_id === userProps.id"
                                    class="mx-2.5 w-[69px] shrink-0 rounded-2lg bg-[#E5EDFF] py-1 text-center md:mr-0"
                                >
                                    <p
                                        class="text-xs font-medium uppercase text-[#6875F5]"
                                    >
                                        Owner
                                    </p>
                                </div>
                            </div>
                            <div
                                class="text-gray-400 transition duration-200 ease-in-out group-hover:text-gray-500"
                                @click.stop="
                                    switchTeamRedirectToSettings(team.id)
                                "
                            >
                                <Cog8ToothIcon class="h-6 w-6" />
                            </div>
                        </div>
                    </div>

                    <inertia-link
                        :href="route('teams.index')"
                        class="group flex cursor-pointer items-center justify-between py-3 pl-3 pr-3 hover:bg-gray-50 hover:text-gray-900 md:pr-4"
                        @click="isOpen = false"
                    >
                        <div class="flex items-center">
                            <p
                                class="ml-2 text-sm font-medium text-gray-500 md:ml-4"
                            >
                                All accounts
                            </p>
                        </div>
                    </inertia-link>

                    <div
                        v-if="invitations && invitations.length"
                        class="mb-[7px] max-h-[140px] overflow-scroll pt-2.5 md:max-h-full md:overflow-hidden"
                        role="none"
                    >
                        <div class="px-6 text-xs uppercase text-gray-400">
                            Invitations
                        </div>
                        <div
                            v-for="invitation in invitations"
                            :key="invitation.id"
                            class="group flex items-center justify-between py-3 pl-6 pr-6 hover:bg-gray-50 hover:text-gray-900 md:pr-4"
                        >
                            <div class="flex items-center">
                                <img
                                    class="h-8 w-8 rounded-full border-gray-300"
                                    :src="invitation.team.profile_photo_url"
                                    alt="Team menu profile photo url 4"
                                />
                                <p
                                    class="ml-2 text-sm font-medium text-gray-500 md:ml-4"
                                >
                                    {{ invitation.team.name }}
                                </p>
                            </div>
                            <div class="flex">
                                <button
                                    type="button"
                                    :disabled="loading"
                                    class="ml-5 rounded-2lg bg-orange-500 px-2 py-1 text-center text-xs font-medium uppercase text-gray-800 disabled:cursor-default disabled:opacity-75 md:ml-2.5"
                                    @click="acceptInvitation(invitation)"
                                >
                                    {{ $t("vue.accept") }}
                                </button>
                                <button
                                    type="button"
                                    :disabled="loading"
                                    class="ml-2.5 rounded-2lg bg-custom-red-600 px-2 py-1 text-center text-xs font-medium uppercase text-white disabled:cursor-default disabled:opacity-75"
                                    @click="refuseInvitation(invitation)"
                                >
                                    Refuse
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mb-5 hidden px-6 pt-[15px] md:block"
                        role="none"
                    >
                        <AtomicButton
                            color="yellow"
                            size="lg"
                            @click="
                                checkAccessOrShowModal() &&
                                handleCreateTeamModal()
                            "
                        >
                            <template #icon>
                                <div class="mr-2">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M9 6.5V9M9 9V11.5M9 9H11.5M9 9H6.5M16.5 9C16.5 9.98491 16.306 10.9602 15.9291 11.8701C15.5522 12.7801 14.9997 13.6069 14.3033 14.3033C13.6069 14.9997 12.7801 15.5522 11.8701 15.9291C10.9602 16.306 9.98491 16.5 9 16.5C8.01509 16.5 7.03982 16.306 6.12987 15.9291C5.21993 15.5522 4.39314 14.9997 3.6967 14.3033C3.00026 13.6069 2.44781 12.7801 2.0709 11.8701C1.69399 10.9602 1.5 9.98491 1.5 9C1.5 7.01088 2.29018 5.10322 3.6967 3.6967C5.10322 2.29018 7.01088 1.5 9 1.5C10.9891 1.5 12.8968 2.29018 14.3033 3.6967C15.7098 5.10322 16.5 7.01088 16.5 9Z"
                                            stroke="#ffffff"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </div>
                            </template>
                            {{ $t("vue.create_new_account") }}
                        </AtomicButton>
                    </div>
                    <div class="pt-[19px] md:hidden">
                        <div
                            class="mx-4 mb-8 flex items-center justify-center rounded-md bg-orange-500 py-1"
                        >
                            <div class="mr-2">
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 6.5V9M9 9V11.5M9 9H11.5M9 9H6.5M16.5 9C16.5 9.98491 16.306 10.9602 15.9291 11.8701C15.5522 12.7801 14.9997 13.6069 14.3033 14.3033C13.6069 14.9997 12.7801 15.5522 11.8701 15.9291C10.9602 16.306 9.98491 16.5 9 16.5C8.01509 16.5 7.03982 16.306 6.12987 15.9291C5.21993 15.5522 4.39314 14.9997 3.6967 14.3033C3.00026 13.6069 2.44781 12.7801 2.0709 11.8701C1.69399 10.9602 1.5 9.98491 1.5 9C1.5 7.01088 2.29018 5.10322 3.6967 3.6967C5.10322 2.29018 7.01088 1.5 9 1.5C10.9891 1.5 12.8968 2.29018 14.3033 3.6967C15.7098 5.10322 16.5 7.01088 16.5 9Z"
                                        stroke="#252F3F"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                            <div
                                class="cursor-pointer text-sm leading-10 text-gray-800"
                                @click="createTeamModal = true"
                            >
                                {{ $t("vue.create_new_team") }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <CreateTeamModal
            v-if="createTeamModal"
            @close="createTeamModal = false"
            @close-and-switch-to-team="handleCloseAndSwitchToTeam"
        />
        <UpgradeRequiredModal
            :opened="showUpgradeModal"
            title="Scale Plan Required"
            description="Creating new accounts requires a Scale plan subscription. Upgrade your subscription to access team management features."
            @close="closeUpgradeModal"
        />
    </div>
</template>

<script lang="ts" setup>
import type IUser from "@/types/Members/IUser";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { router } from "@inertiajs/vue3";
import { usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import route from "ziggy-js";
import { Cog8ToothIcon } from "@heroicons/vue/24/outline";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import type IInvitation from "@/types/Members/IInvitation";
import useTeamMemberAccess from "@/composables/useTeamMemberAccess";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";

defineProps({
    sidebarOpened: {
        default: false,
        type: Boolean,
    },
    sidebar: {
        default: true,
        type: Boolean,
    },
});

onMounted(() => {
    getInvitations();
});
const emit = defineEmits(["closeMenuMobile"]);

const { fetchTeams, getCurrentTeamMembersCount, allTeams } =
    useTeamsAndMembers();

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const userProps = computed(() => globalProps.value.user as IUser);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);
const isOpen = ref(false);
const form = ref({
    id: {} as number,
});
const createTeamModal = ref(false);
const {
    hasTeamMemberAccess,
    showUpgradeModal,
    checkAccessOrShowModal,
    closeUpgradeModal,
} = useTeamMemberAccess();

const invitations = ref<IInvitation[]>([]);
const loading = ref(false);

const getInvitations = async () => {
    try {
        const response = await axios.get(route("user.invitations.index"));

        if (response.data.success) {
            invitations.value = Object.values(response.data.data);
        }
    } catch (err) {
        () => undefined;
    }
};

const acceptInvitation = async (invitation: IInvitation) => {
    loading.value = true;
    router.get(
        route("team.invitation.accept", { token: invitation.token }),
        {},
        {
            preserveState: false,
            onFinish: () => {
                loading.value = false;
            },
            onSuccess: () => {
                getInvitations();
                handleFetchTeams();
                getCurrentTeamMembersCount();
            },
        },
    );
};

const refuseInvitation = async (invitation: IInvitation) => {
    loading.value = true;
    router.get(
        route("team.invitation.refuse", { token: invitation.token }),
        {},
        {
            preserveState: false,
            onFinish: () => {
                loading.value = false;
            },
            onSuccess: () => {
                getInvitations();
                handleFetchTeams();
                getCurrentTeamMembersCount();
            },
        },
    );
};

const current = computed(() => {
    return teamProps.value;
});

const handleProfileMenu = () => {
    emit("closeMenuMobile");
    isOpen.value = !isOpen.value;
};

const handleCreateTeamModal = () => {
    createTeamModal.value = true;
};

const handleCloseMenu = () => {
    isOpen.value = false;
};

const switchTeamRedirectToSettings = (teamId: number) => {
    if (teamProps.value.id === teamId) {
        isOpen.value = false;
        router.get(route("settings.team-settings.index"));
    } else {
        form.value.id = teamId;
        router.post(route("teams.switch.store"), form.value, {
            onSuccess: () => {
                isOpen.value = false;
                router.get(route("settings.team-settings.index"));
                getCurrentTeamMembersCount();
            },
        });
    }
};

const switchTeam = (teamId: number) => {
    if (teamProps.value.id === teamId) {
        isOpen.value = false;
    } else {
        form.value.id = teamId;
        router.post(route("teams.switch.store"), form.value, {
            onSuccess: () => {
                isOpen.value = false;
                getCurrentTeamMembersCount();
            },
        });
    }
};

const handleCloseAndSwitchToTeam = () => {
    createTeamModal.value = false;
    getCurrentTeamMembersCount();
    handleFetchTeams();
};

const handleFetchTeams = () => {
    fetchTeams();
};
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.custom-scroll::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.custom-scroll {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
}
</style>
